/** The quick-peek panel's body for a scope table: the figure's summary and the way to its receipt.
 *
 * Nothing is computed here - every value is the one the row shows - and the receipt link is the same
 * button the cell has (rule 3: a figure that can be read can be traced). Its own file because a
 * component beside a hook in one file defeats fast refresh.
 */

import type { ReactNode } from "react";

import type { Figure } from "../../api/queries";
import { QualityMeter, SourceChip, StatusChip, isOrigin } from "../../components/ui";
import { computedQuantity, tonnes, unitLabel } from "../../lib/format";
import { STATUS_WORD, categoryWords, periodWords, statusOf } from "./model";
import type { FigureStatus, Scope } from "./model";
import styles from "./scopeTable.module.css";

const STATUS_TONE: Record<FigureStatus, "ok" | "warn" | "crit" | "neutral"> = {
  verified: "ok",
  awaiting: "warn",
  not_verified: "neutral",
  gap: "crit",
};

const isGrade = (value: string): value is "A" | "B" | "C" | "D" =>
  value === "A" || value === "B" || value === "C" || value === "D";

export function FigurePeek({
  figure,
  scope,
  onReceipt,
}: {
  figure: Figure;
  scope: Scope;
  onReceipt: (calculationId: string) => void;
}) {
  const status = statusOf(figure);
  const facts: Array<[string, ReactNode]> = [
    ["Site", figure.facility_name],
    ...(scope !== "2" ? ([["Category", categoryWords(figure.category)]] as Array<[string, ReactNode]>) : []),
    ["Period", periodWords(figure.period_start, figure.period_end)],
    ["Quantity", `${computedQuantity(figure.quantity)} ${unitLabel(figure.unit)}`],
    ["Grade", isGrade(figure.quality) ? <QualityMeter grade={figure.quality} /> : figure.quality],
    [
      "Status",
      <StatusChip key="status" tone={STATUS_TONE[status]} size="sm">
        {STATUS_WORD[status]}
      </StatusChip>,
    ],
    ["Source", figure.origin && isOrigin(figure.origin) ? <SourceChip origin={figure.origin} /> : "—"],
  ];
  return (
    <div className={styles.peek} data-testid="figure-peek">
      <button
        type="button"
        className={styles.peekFigure}
        data-testid={`peek-receipt-${figure.id}`}
        aria-label={`Receipt for ${figure.facility_name}, ${tonnes(figure.kgco2e)} tonnes CO2 equivalent`}
        onClick={() => onReceipt(figure.id)}
      >
        {tonnes(figure.kgco2e)}
        <span className={styles.peekUnit}>tCO₂e</span>
      </button>
      <dl className={styles.peekFacts}>
        {facts.map(([label, value]) => (
          <div key={label} className={styles.peekFact}>
            <dt>{label}</dt>
            <dd>{value}</dd>
          </div>
        ))}
      </dl>
    </div>
  );
}
